import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { assignCourts, updateConsecutiveGames } from '../../shared/logic/matchmaking';
import { uid } from '../../shared/utils/helpers';
import { ratingLabel } from '../../shared/logic/scoring';
import type { GameRecord, Session as SessionType, Court } from '../../shared/types';

export default function Session() {
  const { league, players, games, session, sessionId, service } = useApp();
  const [checkedIn, setCheckedIn] = useState<Set<string>>(
    () => new Set(session?.checkedIn ?? [])
  );
  const [sessionCourts, setSessionCourts] = useState<number | ''>(league?.courts ?? 4);
  const [scoreEdits, setScoreEdits] = useState<Record<string, string>>({});

  const playerName = (id: string) => players.find(p => p.id === id)?.name ?? id;

  // ── Check-in ──────────────────────────────────────────────

  function toggleCheckin(id: string) {
    const next = new Set(checkedIn);
    if (next.has(id)) next.delete(id); else next.add(id);
    setCheckedIn(next);
    // Auto-adjust courts
    const leagueMax = league?.courts ?? 4;
    const maxUsable = Math.min(Math.floor(next.size / 4), leagueMax);
    setSessionCourts(maxUsable);
  }

  function selectAll() {
    const next = new Set(players.map(p => p.id));
    setCheckedIn(next);
    const leagueMax = league?.courts ?? 4;
    setSessionCourts(Math.min(Math.floor(next.size / 4), leagueMax));
  }

  function deselectAll() {
    setCheckedIn(new Set());
    setSessionCourts(0);
  }

  // ── Session start ─────────────────────────────────────────

  async function startSession() {
    if (checkedIn.size < 4) { alert('Need at least 4 players checked in.'); return; }
    const ids = [...checkedIn];
    const { courts, waiting } = assignCourts({
      checkedInIds: ids,
      consecutiveGames: {},
      maxCourts: Number(sessionCourts) || 1,
      games,
      players,
    });
    const newSession: SessionType = {
      courts,
      waiting,
      checkedIn: ids,
      benched: [],
      round: 1,
      consecutiveGames: {},
      maxCourts: Number(sessionCourts) || 1,
    };
    await service.setSession(newSession);
  }

  // ── Round close ───────────────────────────────────────────

  async function closeRound(andEnd: boolean) {
    if (!session) return;

    // Gather scores from the UI for non-submitted courts
    const updatedCourts: Court[] = session.courts.map((c, i) => {
      if (c.submitted) return c;
      const sA = scoreEdits[`A-${i}`] ?? '';
      const sB = scoreEdits[`B-${i}`] ?? '';
      return {
        ...c,
        scoreA: sA !== '' ? parseInt(sA) : null,
        scoreB: sB !== '' ? parseInt(sB) : null,
      };
    });

    const hasUnscored = updatedCourts.some(c => c.scoreA == null || c.scoreB == null);
    if (hasUnscored && !confirm('Some courts are missing scores. Close the round anyway?')) return;

    // Save game record
    const game: GameRecord = {
      id: uid(),
      date: new Date().toISOString(),
      session: sessionId,
      round: session.round,
      courts: updatedCourts,
    };
    await service.addGame(game);

    if (andEnd) {
      await service.setSession(null);
      localStorage.setItem('rp_last_session_id', sessionId);
      localStorage.removeItem('rp_session_id');
      setScoreEdits({});
    } else {
      const newConsec = updateConsecutiveGames({ ...session, courts: updatedCourts });
      const { courts, waiting } = assignCourts({
        checkedInIds: session.checkedIn,
        benchedIds: session.benched ?? [],
        consecutiveGames: newConsec,
        maxCourts: session.maxCourts,
        games: [...games, game],
        players,
      });
      const nextSession: SessionType = {
        ...session,
        courts,
        waiting,
        round: session.round + 1,
        consecutiveGames: newConsec,
      };
      await service.setSession(nextSession);
      setScoreEdits({});
    }
  }

  async function reshuffleCourts() {
    if (!session) return;
    if (!confirm('Reshuffle? All submitted scores this round will be lost.')) return;
    const { courts, waiting } = assignCourts({
      checkedInIds: session.checkedIn,
      benchedIds: session.benched ?? [],
      consecutiveGames: session.consecutiveGames,
      maxCourts: session.maxCourts,
      games,
      players,
    });
    await service.setSession({ ...session, courts, waiting });
    setScoreEdits({});
  }

  async function benchPlayer(id: string) {
    if (!session) return;
    const current = session.benched ?? [];
    await service.setSession({
      ...session,
      benched: [...current, id],
      waiting: session.waiting.filter(w => w !== id),
    });
  }

  async function returnPlayer(id: string) {
    if (!session) return;
    const current = session.benched ?? [];
    await service.setSession({
      ...session,
      benched: current.filter(b => b !== id),
    });
  }

  // ── Computed ──────────────────────────────────────────────

  const benchedIds   = session?.benched ?? [];
  const waitingList  = (session?.waiting ?? []).filter(id => !benchedIds.includes(id));
  const submittedCount = session?.courts.filter(c => c.submitted).length ?? 0;
  const totalCourts  = session?.courts.length ?? 0;
  const isSmartMatch = (session?.maxCourts ?? 0) >= 3 && games.length >= 8;

  // ══════════════════════════════════════════════════════════
  //  RENDER — Check-in phase
  // ══════════════════════════════════════════════════════════

  if (!session) {
    const maxUsable = Math.min(Math.floor(checkedIn.size / 4), league?.courts ?? 4);
    const waiting = checkedIn.size - maxUsable * 4;

    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Check-In <span style={{ fontWeight: 400, fontSize: '.85rem', color: 'var(--gray-500)' }}>({checkedIn.size} selected)</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={selectAll}>All</button>
              <button className="btn btn-secondary btn-sm" onClick={deselectAll}>None</button>
            </div>
          </div>

          {!players.length ? (
            <div className="empty">
              <div className="empty-icon">👤</div>
              Add players first.
            </div>
          ) : (
            <div className="player-grid">
              {players.map(p => {
                const checked = checkedIn.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`player-card ${checked ? 'checked-in' : ''}`}
                    onClick={() => toggleCheckin(p.id)}
                  >
                    <div className="pname">{p.name} {checked ? '✅' : ''}</div>
                    <div className="meta">
                      <span className="badge badge-blue">{ratingLabel(p)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Courts for this session */}
        {checkedIn.size >= 4 && (
          <div className="card">
            <div className="card-title">Courts for This Session</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="number"
                min={1}
                max={league?.courts ?? 4}
                value={sessionCourts}
                onChange={e => setSessionCourts(e.target.value === '' ? '' : Math.min(parseInt(e.target.value), league?.courts ?? 4))}
                style={{ width: 72 }}
              />
              <span style={{ fontSize: '.85rem', color: 'var(--gray-500)' }}>
                (League default: {league?.courts ?? 4})
              </span>
            </div>
            {waiting > 0 && (
              <div className="alert alert-info" style={{ marginTop: 10 }}>
                ℹ️ With {maxUsable} court{maxUsable !== 1 ? 's' : ''}, <strong>{waiting} player{waiting !== 1 ? 's' : ''}</strong> will be on the waiting list this round.
              </div>
            )}
            <button
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 14 }}
              onClick={startSession}
            >
              🏟 Start Session ({checkedIn.size} players, {maxUsable} courts)
            </button>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  //  RENDER — Active session
  // ══════════════════════════════════════════════════════════

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>

      {/* Round header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontWeight: 800, fontSize: '1.3rem' }}>Round {session.round}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={reshuffleCourts}>🔀 Reshuffle</button>
          <button className="btn btn-yellow btn-sm" onClick={() => closeRound(false)}>✔ Next Round</button>
          <button className="btn btn-danger btn-sm" onClick={() => closeRound(true)}>⏹ End Session</button>
        </div>
      </div>

      {/* Submission status */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
        {submittedCount === totalCourts ? (
          <span style={{ color: 'var(--green-d)', fontWeight: 700 }}>
            ✅ All {totalCourts} courts submitted — ready to close the round.
          </span>
        ) : (
          <span>
            ⏳ <strong>{submittedCount} of {totalCourts}</strong> courts submitted via kiosk. Enter missing scores below.
          </span>
        )}
      </div>

      {/* Courts */}
      {session.courts.map((c, i) => (
        <div
          key={i}
          className="court-card"
          style={{ borderColor: c.submitted ? 'var(--green)' : undefined }}
        >
          <div className="court-header">
            <div className="court-title">🏟 Court {i + 1}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {c.submitted
                ? <span className="badge badge-green">✓ Submitted</span>
                : <span className="badge badge-gray">Pending</span>
              }
              <span className={`badge ${isSmartMatch ? 'badge-blue' : 'badge-gray'}`}>
                {isSmartMatch ? 'Smart Match' : 'Random'}
              </span>
            </div>
          </div>

          <div className="teams">
            <div className="team">
              <div className="team-label">Team A</div>
              {c.teamA.map(id => <div key={id} className="team-player">{playerName(id)}</div>)}
            </div>
            <div className="vs">VS</div>
            <div className="team">
              <div className="team-label">Team B</div>
              {c.teamB.map(id => <div key={id} className="team-player">{playerName(id)}</div>)}
            </div>
          </div>

          {!c.submitted && (
            <div className="score-row" style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--gray-500)' }}>Score:</span>
              <span style={{ fontSize: '.82rem' }}>Team A</span>
              <input
                className="score-input"
                type="number" min={0} max={99}
                value={scoreEdits[`A-${i}`] ?? (c.scoreA != null ? c.scoreA : '')}
                placeholder="0"
                onChange={e => setScoreEdits(prev => ({ ...prev, [`A-${i}`]: e.target.value }))}
              />
              <span style={{ fontWeight: 700, color: 'var(--gray-500)' }}>–</span>
              <input
                className="score-input"
                type="number" min={0} max={99}
                value={scoreEdits[`B-${i}`] ?? (c.scoreB != null ? c.scoreB : '')}
                placeholder="0"
                onChange={e => setScoreEdits(prev => ({ ...prev, [`B-${i}`]: e.target.value }))}
              />
              <span style={{ fontSize: '.82rem' }}>Team B</span>
            </div>
          )}

          {c.submitted && (
            <div style={{ marginTop: 10, textAlign: 'center', fontWeight: 800, fontSize: '1.2rem', color: 'var(--green-d)' }}>
              {c.scoreA} – {c.scoreB}
            </div>
          )}
        </div>
      ))}

      {/* Waiting list */}
      {waitingList.length > 0 && (
        <div className="card">
          <div className="card-title">⏳ Sitting Out This Round</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {waitingList.map(id => {
              const streak = session.consecutiveGames[id] ?? 0;
              return (
                <div key={id} style={{
                  background: '#fff', border: '1.5px solid var(--gray-300)',
                  borderRadius: 99, padding: '4px 12px', fontSize: '.82rem', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {playerName(id)}
                  {streak >= 3 && <span className="badge badge-red">3-game rest</span>}
                  {streak > 0 && streak < 3 && <span className="badge badge-gray">{streak}-game streak</span>}
                  <button
                    onClick={() => benchPlayer(id)}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer',
                      fontSize: '.75rem', color: 'var(--gray-400)', padding: '0 2px',
                    }}
                    title="Bench this player"
                  >
                    🚫
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Benched players */}
      {benchedIds.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--gray-300)' }}>
          <div className="card-title" style={{ color: 'var(--gray-500)' }}>🚫 Benched</div>
          <div style={{ fontSize: '.82rem', color: 'var(--gray-500)', marginBottom: 10 }}>
            These players are out for the rest of the session. Tap Return to bring them back.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {benchedIds.map(id => (
              <div key={id} style={{
                background: 'var(--gray-100)', border: '1.5px solid var(--gray-300)',
                borderRadius: 99, padding: '4px 14px', fontSize: '.82rem', fontWeight: 600,
                color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                {playerName(id)}
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ padding: '2px 10px', fontSize: '.75rem', borderRadius: 99 }}
                  onClick={() => returnPlayer(id)}
                >
                  ↩ Return
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
